// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ChartContainer } from '@shared/ui-kit/components';
import { formatToPercent } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface SectorCompositionProps {
  readonly categories: string[];
  readonly percentages: number[];
  readonly benchmarks?: (number | null)[];
  readonly columns?: Partial<Record<'portfolio' | 'benchmark', boolean>>;
}

export const SectorComposition: FC<SectorCompositionProps> = ({
  categories,
  percentages,
  columns = { benchmark: true, portfolio: true },
  benchmarks = [],
}) => {
  const { t } = useTranslation();

  const data = useMemo(
    () =>
      categories
        .map((category, index) => ({
          name: category,
          portfolio: percentages.at(index) ?? 0,
          benchmark: benchmarks.at(index) ?? 0,
        }))
        .sort(
          ({ portfolio }, { portfolio: portfolio2 }) => +portfolio2 - +portfolio,
        ),
    [categories, percentages, benchmarks],
  );

  const maxPortfolio = useMemo(
    () =>
      data.length > 0 ? Math.max(...data.map(({ portfolio }) => +portfolio)) : 100,
    [data],
  );

  return (
    <ChartContainer
      header={t('glossary.sector-composition', { ns: 'glossary' })}
      popupName="charts.sectorComposition"
    >
      <table
        className="w-full text-primary-medium"
        data-testid="sector-composition-table"
      >
        <thead>
          <tr>
            <th />
            <th className="hidden w-1/3 min-w-[107px] pr-6 xl:table-cell" />
            {columns.portfolio ? (
              <th className="text-right font-normal uppercase">
                {t('glossary.portfolio', { ns: 'glossary' })}
              </th>
            ) : null}
            {columns.benchmark ? (
              <th className="text-right font-normal uppercase">
                {t('glossary.benchmark', { ns: 'glossary' })}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {Array.from(data, ({ name, portfolio, benchmark }, key) => (
            <tr key={key} className="[&>td]:py-2">
              <td className="pr-4">
                <span className="flex text-ellipsis text-left">{name}</span>
              </td>
              <td className="hidden w-1/3 min-w-[107px] pr-6 pt-1 xl:table-cell">
                <progress
                  className="progress"
                  value={portfolio}
                  max={maxPortfolio}
                />
              </td>
              {columns.portfolio ? (
                <td className=" w-1/6">
                  <span className="!money-display block text-right">
                    {formatToPercent(portfolio)}
                  </span>
                </td>
              ) : null}
              {columns.benchmark ? (
                <td className="w-1/6 text-right">
                  <span className="!money-display block font-[poppins]">
                    {formatToPercent(benchmark)}
                  </span>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </ChartContainer>
  );
};
